import RemoveModeratorIcon from "@mui/icons-material/RemoveModerator";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useAppData } from "store/appData";
import t from "../translations";
import { useDatasetDetail } from "./DatasetDetailContext";
import { SUPERVISOR_VERIFIED_STATUSES } from "./imageWorkflowStatus";

// Toolbar action that reverts the open image's supervisor-verified detections
// (Supervisor Approved -> True Positive, Supervisor False Positive -> False
// Positive, within the dataset's type_list) behind a confirm dialog.
export default function UnsuperverifyButton() {
  const {
    datasetId,
    kind,
    selectedImage,
    annotations,
    datasetTypeList,
    refreshAnnotations,
    unsuperverify,
  } = useDatasetDetail();
  const { objectTypes } = useAppData();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const revertibleCount = useMemo(() => {
    if (datasetTypeList.length === 0) return 0;
    const allowed = new Set(datasetTypeList);
    // For defect datasets the backend only reverts classifications on defect
    // types (issue=true), so we apply the same filter here to keep the count
    // consistent with what will actually be reverted.
    return annotations.reduce(
      (total, annotation) =>
        total +
        annotation.types.filter(
          (type) =>
            type.workflow_status !== null &&
            SUPERVISOR_VERIFIED_STATUSES.has(type.workflow_status) &&
            allowed.has(type.object_type_id) &&
            (kind !== "defect" || objectTypes[type.object_type_id]?.issue === true)
        ).length,
      0
    );
  }, [annotations, datasetTypeList, kind, objectTypes]);

  const disabled = revertibleCount === 0;

  const handleConfirm = async () => {
    if (!selectedImage) return;
    setSubmitting(true);
    try {
      const updated = await unsuperverify(
        selectedImage.region,
        selectedImage.project_id,
        datasetId,
        selectedImage.id
      );
      toast.success(t.UnsuperverifySuccess(updated));
      setOpen(false);
      // Refresh in the background; loadAnnotations handles its own errors.
      void refreshAnnotations();
    } catch (error) {
      captureException(error, {
        tags: { feature: "datasetDetail.unsuperverify" },
      });
      toast.error(t.UnsuperverifyError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip
        title={
          disabled ? t.UnsuperverifyNothingTooltip : t.UnsuperverifyTooltip
        }
        arrow
      >
        {/* span keeps the tooltip working while the button is disabled */}
        <Box component="span">
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => setOpen(true)}
            data-testid="datasetImagePanel.unsuperverify"
            aria-label={t.Unsuperverify}
          >
            <RemoveModeratorIcon fontSize="small" />
          </IconButton>
        </Box>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => (submitting ? undefined : setOpen(false))}
      >
        <DialogTitle>{t.UnsuperverifyTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t.UnsuperverifyBody(revertibleCount)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            {t.UnsuperverifyCancel}
          </Button>
          <Button
            onClick={handleConfirm}
            color="warning"
            variant="contained"
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            data-testid="datasetImagePanel.unsuperverify.confirm"
          >
            {t.UnsuperverifyConfirm}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
